import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/endpoints';
import Avatar from '@/components/common/Avatar';
import Pagination from '@/components/common/Pagination';
import { Alert, Empty, ErrorState, Loading } from '@/components/common/State';
import { formatDate } from '@/utils/format';

const ROLES = ['ROLE_USER', 'ROLE_MANAGER', 'ROLE_ADMIN'];
const STATUSES = ['ACTIVE', 'DORMANT', 'SUSPENDED', 'WITHDRAWN'];

export default function AdminUserListPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'users', page, keyword],
    queryFn: () => adminApi.users({ page, size: 20, keyword: keyword || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) => adminApi.changeUserRole(userId, role),
    onSuccess: () => {
      setFeedback('권한을 변경했습니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const changeStatus = useMutation({
    mutationFn: ({ userId, status }: { userId: number; status: string }) =>
      adminApi.changeUserStatus(userId, status),
    onSuccess: () => {
      setFeedback('상태를 변경했습니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  return (
    <>
      <h1 className="panel-title">회원 관리</h1>
      {feedback && <Alert tone="info">{feedback}</Alert>}

      <input
        className="input"
        style={{ maxWidth: 260, marginBottom: 16 }}
        placeholder="이메일 / 이름 / 닉네임"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setKeyword(e.currentTarget.value);
            setPage(1);
          }
        }}
      />

      {isLoading && <Loading />}
      {isError && <ErrorState error={error} onRetry={refetch} />}

      {data &&
        (data.content.length === 0 ? (
          <Empty title="회원이 없습니다." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>이메일</th>
                    <th>이름</th>
                    <th>가입경로</th>
                    <th>권한</th>
                    <th>상태</th>
                    <th>최근 로그인</th>
                    <th>가입일</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((user) => (
                    <tr key={user.userId}>
                      <td>{user.userId}</td>
                      <td className="wrap">{user.email}</td>
                      <td>
                        <span className="writer">
                          <Avatar src={user.profileImageUrl} size={28} />
                          {user.name}
                        </span>
                      </td>
                      <td>{user.provider}</td>
                      <td>
                        <select
                          className="select"
                          style={{ width: 130, padding: '4px 8px' }}
                          value={user.role}
                          onChange={(e) => changeRole.mutate({ userId: user.userId, role: e.target.value })}
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>
                              {role.replace('ROLE_', '')}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="select"
                          style={{ width: 120, padding: '4px 8px' }}
                          value={user.status}
                          onChange={(e) =>
                            changeStatus.mutate({ userId: user.userId, status: e.target.value })
                          }
                        >
                          {STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{formatDate(user.lastLoginAt, true)}</td>
                      <td>{formatDate(user.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.page} totalPages={data.totalPages} onChange={setPage} />
          </>
        ))}
    </>
  );
}
